import { Component, OnInit } from '@angular/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { GetRowIdParams, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { AccountUser, ActiveStatusEnum } from '@models';
import { ProgramAccessItem, ProgramRulesService } from '../services/program-rules.service';
import { ProgramNarrativeDialogComponent } from '../programs/program-narrative-dialog.component';
import { AgFns, GridOptionsExt } from '@core';
import { DateFns, UtilFns } from '@utils';

export interface AccountUserProgramsDialogParams {
  accountUser: AccountUser;
}

@Component({
  selector: 'prox-account-user-programs-dialog',
  standalone: false,
  templateUrl: './account-user-programs-dialog.component.html',
})
export class AccountUserProgramsDialogComponent implements OnInit {
  programAccessItems: ProgramAccessItem[] = [];
  filteredProgramAccessItems: ProgramAccessItem[] = [];
  isLoading = true;
  accountUser!: AccountUser;
  showExpiredAndInactive = false;
  selectedItem: ProgramAccessItem | null = null;

  gridOptions: GridOptionsExt = {};

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig<AccountUserProgramsDialogParams>,
    private dialogService: DialogService,
    private programRulesService: ProgramRulesService,
  ) {}

  public static async openDialog(
    dialogService: DialogService,
    data: AccountUserProgramsDialogParams,
    config?: DynamicDialogConfig<AccountUserProgramsDialogParams>,
  ) {
    const ref = dialogService.open(AccountUserProgramsDialogComponent, {
      closable: false,
      draggable: true,
      modal: true,
      width: '75vw',
      height: '85vh',
      focusOnShow: false,
      ...config,
      data: data,
      header: `Programs Available to ${data.accountUser.proximityUser?.fullName || 'User'}`,
    });
    return firstValueFrom(ref.onClose);
  }

  ngOnInit() {
    this.accountUser = this.config.data!.accountUser;
    this.initGrid();
    this.loadData();
  }

  initGrid() {
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      rowModelType: 'clientSide',
      rowSelection: 'single',
      onRowSelected: this.onRowSelected,
      getRowId: (params: GetRowIdParams) => {
        const item = params.data as ProgramAccessItem;
        return `${item.program.id}-${item.programUserGroupId}`;
      },
      overlayNoRowsTemplate: '<span class="ag-overlay-no-rows-center">No Active Programs for This Account User</span>',
    });
  }

  onRowSelected = (event: RowSelectedEvent) => {
    if (event.node.isSelected()) {
      this.selectedItem = event.data as ProgramAccessItem;
    }
  };

  onGridReady = (event?: GridReadyEvent) => {
    const colDefs = [
      { headerName: 'Program', field: 'program.name', ...AgFns.flexWidth(180), sort: 'asc' as const },
      { headerName: 'Program User Group', field: 'programUserGroupName', ...AgFns.flexWidth(140) },
      {
        // Effective orderability: user has no exclusion, program isn't expired, and both
        // Program + PUG are Active. Mirrors the same rule used in the ledger Overview's
        // Ordering Status readout.
        headerName: 'Available?',
        colId: 'isAvailable',
        ...AgFns.fixWidth(120),
        valueGetter: (params: any) => {
          const item = params.data as ProgramAccessItem;
          return this.isAvailable(item) ? 'Yes' : 'No';
        },
        cellRenderer: (params: any) => {
          const item = params.data as ProgramAccessItem;
          return this.isAvailable(item)
            ? '<span class="text-green-600 font-bold">Yes</span>'
            : '<span class="text-red-600 font-bold">No</span>';
        },
      },
      {
        headerName: 'Program Status',
        field: 'displayStatus',
        ...AgFns.fixWidth(120),
        cellRenderer: (params: any) => {
          const item = params.data as ProgramAccessItem;
          if (item.isExpired) {
            return '<span class="text-red-600 font-bold">Expired</span>';
          }
          const status = params.value || '';
          if (item.program.activeStatusId === ActiveStatusEnum.Inactive) {
            return `<span class="text-red-600 font-bold">${status}</span>`;
          }
          return status;
        },
      },
      {
        headerName: 'PUG Status',
        field: 'programUserGroupStatus',
        ...AgFns.fixWidth(120),
        cellRenderer: (params: any) => {
          const item = params.data as ProgramAccessItem;
          const status = params.value || '';
          if (item.programUserGroupActiveStatusId === ActiveStatusEnum.Inactive) {
            return `<span class="text-red-600 font-bold">${status}</span>`;
          }
          return status;
        },
      },
      {
        headerName: 'Start Date',
        field: 'program.startDate',
        ...AgFns.fixWidth(140),
        valueFormatter: (params: any) => (params.value ? DateFns.fmtDate(params.value) : ''),
      },
      {
        headerName: 'End Date',
        field: 'program.endDate',
        ...AgFns.fixWidth(140),
        valueFormatter: (params: any) => (params.value ? DateFns.fmtDate(params.value) : ''),
      },
      {
        headerName: 'Excluded',
        field: 'isExcluded',
        ...AgFns.fixWidth(120),
        cellRenderer: (params: any) => (params.value ? '<span class="text-red-600 font-bold">Yes</span>' : 'No'),
      },
      {
        headerName: 'Issuance',
        field: 'isIssuanceProgram',
        ...AgFns.fixWidth(120),
        cellRenderer: (params: any) => (params.value ? '<span class="text-green-600 font-bold">Yes</span>' : 'No'),
      },
      {
        headerName: 'Allowance',
        field: 'isAllowanceProgram',
        ...AgFns.fixWidth(120),
        cellRenderer: (params: any) => (params.value ? '<span class="text-green-600 font-bold">Yes</span>' : 'No'),
      },
    ];

    AgFns.initGrid(event ?? this.gridOptions, colDefs);
  };

  onShowExpiredChange() {
    this.applyExpiredFilter();
  }

  private applyExpiredFilter() {
    if (this.showExpiredAndInactive) {
      this.filteredProgramAccessItems = this.programAccessItems;
    } else {
      this.filteredProgramAccessItems = this.programAccessItems.filter(
        (item) => !item.isExpired && item.program.activeStatusId !== ActiveStatusEnum.Inactive,
      );
    }
  }

  async loadData() {
    try {
      // Use the service to get program access items
      this.programAccessItems = await this.programRulesService.getProgramAccessItemsForUser(this.accountUser);

      // Apply the expired filter
      this.applyExpiredFilter();
    } catch (error) {
      console.error('Error loading program access:', error);
    } finally {
      this.isLoading = false;
    }
  }

  formatCurrency(value: number | null): string {
    return value !== null ? UtilFns.fmtCurrency(value) : '';
  }

  /** Effective orderability for this row: user can place an order on this Program via this PUG. */
  private isAvailable(item: ProgramAccessItem): boolean {
    return (
      !item.isExcluded &&
      !item.isExpired &&
      item.program.activeStatusId === ActiveStatusEnum.Active &&
      item.programUserGroupActiveStatusId === ActiveStatusEnum.Active
    );
  }

  showProgramNarrative() {
    if (this.selectedItem) {
      ProgramNarrativeDialogComponent.openDialog(this.dialogService, { programId: this.selectedItem.program.id });
    }
  }

  close() {
    this.ref.close();
  }
}
